/* eslint-disable react/no-unstable-nested-components */
import {
    ActionButton,
    Button,
    Card,
    DatePicker,
    Dialog,
    DialogContent,
    Dropdown,
    DropdownContent,
    DropdownItem,
    DropdownTrigger,
    FilterButton,
    Popover,
    PopoverContent,
    PopoverTrigger,
    SearchInput,
    Table,
} from "@jhool-io/fe-components";
import {
    ColumnDef,
    getCoreRowModel,
    useReactTable,
    VisibilityState,
} from "@tanstack/react-table";
import { Link, useSearchParams } from "react-router-dom";
import * as React from "react";
import { format } from "date-fns";
import { useIsMutating, useQueryClient } from "@tanstack/react-query";
import {
    useExportExpiringConsents,
    useGetExpiringConsents,
} from "../../hooks/queries/reports.queries";
import { useUpdateConsent } from "../../hooks/mutations/reports.mutations";
import {
    displayNameInRightFormat,
    formatAgeInMonths,
    formatDate,
    formatExpiryStatus,
    handleDisplayClientEmailCorrectly,
    convertDateFilterStringToDate,
    appendMidnightTimeToDate,
    cn,
    makeStringFirstLetterCapital,
    removeEnumUnderscore,
} from "../../../../utils/helpers";
import {
    IExpiringConsent,
    IConsentReport,
    ConsentStatus,
    IConsentDocument,
} from "../../types/reports.types";
import ListState from "../../../../components/ListState/ListState";
import Loader from "../../../../shared-ui/Loader/Loader";
import { useDebounce } from "../../../../hooks/helpers";
import useToast from "../../../../hooks/useToast";
import {
    getConsentStatusDisplay,
    getConsentStatusTag,
} from "../../helpers/reports.helper";
import EditIcon from "../../../../icons/Edit";
import CheckIcon from "../../../../icons/Check";
import TimesIcon from "../../../../icons/Times";
import EyeIcon from "../../../../icons/Eye";
import UploadIcon from "../../../../icons/Upload";
import UploadDocumentForm from "./UploadDocumentForm";
import ActivityLogModal from "./ActivityLogModal";
import TableFiltersDisplay from "../../../../shared-ui/TableFiltersDisplay/TableFiltersDisplay";
import RequestConsentCorrectionForm from "./RequestConsentCorrectionForm";
import ConsentDocumentViewer from "../ConsentDocumentViewer/ConsentDocumentViewer";

type Dialogs = "upload" | "view" | "activity";

export default function ExpiringConsentsReportsList() {
    const [searchParams, setSearchParams] = useSearchParams();
    const queryClient = useQueryClient();
    const { toast } = useToast();
    const updateConsentMutation = useUpdateConsent();
    const isUploadDocumentMutating = useIsMutating([
        "upload-consent-documents",
    ]);
    const isUpdateConsentMutating = useIsMutating(["update-consent"]);

    const [isExporting, setIsExporting] = React.useState(false);
    const [columnVisibility, setColumnVisibility] = React.useState<
        Record<string, boolean>
    >(() => {
        const stored = localStorage.getItem(
            "expiringconsents_columns_visibility"
        );
        if (!stored) return {};
        const parsed = JSON.parse(stored);
        if (parsed.actions === false) {
            delete parsed.actions;
        }
        return parsed;
    });
    const [dialogInView, setDialogInView] = React.useState<Dialogs | null>(
        null
    );
    const [showDocumentViewer, setShowDocumentViewer] = React.useState(false);
    const [selectedDocuments, setSelectedDocuments] = React.useState<
        IConsentDocument[]
    >([]);
    const [selectedConsentForViewer, setSelectedConsentForViewer] =
        React.useState<IExpiringConsent | null>(null);
    const [currentDocumentIndex, setCurrentDocumentIndex] = React.useState(0);
    const [selectedConsentForUpload, setSelectedConsentForUpload] =
        React.useState<string | null>(null);
    const [showActivityLogModal, setShowActivityLogModal] =
        React.useState(false);
    const [selectedConsentForActivity, setSelectedConsentForActivity] =
        React.useState<IExpiringConsent | null>(null);
    const [selectedConsentForCorrection, setSelectedConsentForCorrection] =
        React.useState<IExpiringConsent | null>(null);
    const [editingConsentId, setEditingConsentId] = React.useState<
        string | null
    >(null);
    const [editedSignedDate, setEditedSignedDate] = React.useState<Date | null>(
        null
    );

    const searchString = searchParams.get("search") || "";
    const statusFilter = searchParams.get("status") || "";
    const startDateFromFilter = searchParams.get("start_date_from");
    const startDateToFilter = searchParams.get("start_date_to");
    const search = useDebounce(searchString, 500);
    const startDateFromFilterToDate =
        convertDateFilterStringToDate(startDateFromFilter);
    const startDateToFilterToDate =
        convertDateFilterStringToDate(startDateToFilter);

    const { data, error, isLoading, isSuccess } = useGetExpiringConsents({
        search_string: search,
        status: statusFilter as ConsentStatus,
        start_date_from: startDateFromFilter || undefined,
        start_date_to: startDateToFilter || undefined,
    });

    const handleSetSearchString = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.value === "") searchParams.delete("search");
        else searchParams.set("search", e.target.value);
        searchParams.set("page", "1");
        localStorage.setItem(
            "expiringconsents_filters",
            searchParams.toString()
        );
        setSearchParams(searchParams);
    };

    const handleSetStartDateFromFilter = (date: Date | null) => {
        if (date === null) searchParams.delete("start_date_from");
        else searchParams.set("start_date_from", format(date, "yyyy-MM-dd"));
        localStorage.setItem(
            "expiringconsents_filters",
            searchParams.toString()
        );
        setSearchParams(searchParams);
    };

    const handleSetStartDateToFilter = (date: Date | null) => {
        if (date === null) searchParams.delete("start_date_to");
        else searchParams.set("start_date_to", format(date, "yyyy-MM-dd"));
        localStorage.setItem(
            "expiringconsents_filters",
            searchParams.toString()
        );
        setSearchParams(searchParams);
    };

    const handleSetStatusFilter = (status: string) => {
        if (status === "") searchParams.delete("status");
        else searchParams.set("status", status);
        searchParams.set("page", "1");
        localStorage.setItem(
            "expiringconsents_filters",
            searchParams.toString()
        );
        setSearchParams(searchParams);
    };

    const handleViewActivityLog = (consent: IExpiringConsent) => {
        setSelectedConsentForActivity(consent);
        setShowActivityLogModal(true);
    };

    const handleViewDocuments = (consent: IExpiringConsent) => {
        if (consent.consent_documents.length > 0) {
            setSelectedDocuments(consent.consent_documents);
            setSelectedConsentForViewer(consent);
            setCurrentDocumentIndex(0);
            setShowDocumentViewer(true);
        }
    };

    const handleCloseDocumentViewer = () => {
        setShowDocumentViewer(false);
        setSelectedDocuments([]);
        setSelectedConsentForViewer(null);
        setCurrentDocumentIndex(0);
    };

    const handleOpenUploadModal = (consentId: string) => {
        setSelectedConsentForUpload(consentId);
        setDialogInView("upload");
    };

    const handleCloseUploadModal = () => {
        setDialogInView(null);
        setSelectedConsentForUpload(null);
    };

    const handleEditConsent = React.useCallback((consent: IExpiringConsent) => {
        setEditingConsentId(consent.consent_id);
        setEditedSignedDate(
            consent.signed_date
                ? convertDateFilterStringToDate(consent.signed_date)
                : null
        );
    }, []);

    const handleCancelEdit = () => {
        setEditingConsentId(null);
        setEditedSignedDate(null);
    };

    const handleSaveEdit = React.useCallback(
        (originalConsent: IExpiringConsent) => {
            const updateData: Record<string, string | null> = {};

            const originalSignedDate =
                originalConsent.signed_date?.split("T")[0] || "";

            const editedSignedDateStr = editedSignedDate
                ? editedSignedDate.toISOString().split("T")[0]
                : "";

            if (editedSignedDateStr !== originalSignedDate) {
                if (editedSignedDateStr) {
                    updateData.signed_date = editedSignedDateStr;
                }
            }

            if (Object.keys(updateData).length === 0) {
                handleCancelEdit();
                return;
            }

            updateConsentMutation.mutate(
                {
                    consentId: originalConsent.consent_id,
                    data: updateData,
                },
                {
                    onSuccess: (response) => {
                        queryClient.invalidateQueries(["expiring-consents"]);
                        toast({
                            mode: "success",
                            message:
                                response.message ||
                                "Consent updated successfully",
                        });
                        handleCancelEdit();
                    },
                    onError: (err) => {
                        toast({
                            mode: "error",
                            message:
                                err.response?.data.message ||
                                "Could not update consent at this time",
                        });
                    },
                }
            );
        },
        [updateConsentMutation, queryClient, toast, editedSignedDate]
    );

    const handleChangeConsentStatus = React.useCallback(
        (consent: IExpiringConsent, status: ConsentStatus) => {
            if (status === ConsentStatus.NEEDS_CORRECTION) {
                setSelectedConsentForCorrection(consent);
                return;
            }
            const changeStatusPayload = {
                consentId: consent.consent_id,
                data: { status },
            };
            updateConsentMutation.mutate(changeStatusPayload, {
                onSuccess: (response) => {
                    queryClient.invalidateQueries(["expiring-consents"]);
                    toast({
                        mode: "success",
                        message:
                            response.message ||
                            "Consent status updated successfully",
                    });
                },
                onError: (err) => {
                    toast({
                        mode: "error",
                        message:
                            err.response?.data.message ||
                            "Could not update consent status at this time",
                    });
                },
            });
        },
        [updateConsentMutation, queryClient, toast]
    );

    const handleColumnVisibilityChange = (
        updaterOrValue:
            | VisibilityState
            | ((old: VisibilityState) => VisibilityState)
    ) => {
        setColumnVisibility((prevState) => {
            const newState =
                typeof updaterOrValue === "function"
                    ? updaterOrValue(prevState)
                    : updaterOrValue;

            localStorage.setItem(
                "expiringconsents_columns_visibility",
                JSON.stringify(newState)
            );

            return newState;
        });
    };

    const columns: ColumnDef<IExpiringConsent>[] = React.useMemo(
        () => [
            {
                accessorKey: "client._id",
                header: "Client Name",
                cell: ({ row }) => {
                    return (
                        <Link
                            to={`/clients/${row.original.client_id}`}
                            className="font-medium text-textLink underline"
                            onClick={(e) => e.stopPropagation()}
                        >
                            {displayNameInRightFormat({
                                firstName:
                                    row.original.client.client_first_name,
                                lastName: row.original.client.client_last_name,
                            })}
                        </Link>
                    );
                },
            },
            {
                accessorKey: "client.client_email",
                header: "Contact",
                cell: ({ row }) => (
                    <div className="flex flex-col">
                        <span className="truncate max-w-[200px] block">
                            {row.original.client.client_email
                                ? handleDisplayClientEmailCorrectly(
                                      row.original.client.client_email
                                  )
                                : "--"}
                        </span>
                        <span className="text-sm text-gray-500">
                            {row.original.client.client_phone || "--"}
                        </span>
                    </div>
                ),
            },
            {
                accessorKey: "client.client_date_of_birth",
                header: "Date of Birth",
                cell: ({ row }) => {
                    const dob = row.original.client.client_date_of_birth;
                    if (!dob) return "--";

                    return (
                        <div className="flex flex-col">
                            <span>
                                {formatDate(
                                    appendMidnightTimeToDate(dob),
                                    true
                                )}
                            </span>
                            <span className="text-sm text-gray-500">
                                ({formatAgeInMonths(row.original.age_in_months)}
                                )
                            </span>
                        </div>
                    );
                },
            },
            {
                accessorKey: "provider.provider_id",
                header: "Provider Name",
                cell: ({ row }) => (
                    <div className="flex flex-col">
                        <span className="font-medium">
                            {row.original.provider
                                ? displayNameInRightFormat({
                                      firstName:
                                          row.original.provider
                                              .provider_first_name,
                                      lastName:
                                          row.original.provider
                                              .provider_last_name,
                                  })
                                : "--"}
                        </span>
                        <span className="text-sm text-gray-500">
                            {makeStringFirstLetterCapital(
                                removeEnumUnderscore(
                                    row.original.session.recurrence_rule
                                )
                            )}{" "}
                            •{" "}
                            {makeStringFirstLetterCapital(
                                removeEnumUnderscore(
                                    row.original.session.delivery_option
                                )
                            )}
                        </span>
                    </div>
                ),
            },
            {
                accessorKey: "signed_date",
                header: "Signed Date",
                cell: ({ row }) => {
                    const signedDate = row.original.signed_date;
                    const isEditing =
                        editingConsentId === row.original.consent_id;

                    return isEditing ? (
                        <div
                            onClick={(e) => e.stopPropagation()}
                            onKeyDown={(e) => e.stopPropagation()}
                            role="button"
                            tabIndex={0}
                            className="w-32"
                        >
                            <DatePicker
                                selected={editedSignedDate}
                                onChange={(date) => setEditedSignedDate(date)}
                                placeholderText="MM/DD/YYYY"
                                isClearable
                                isInDialog
                            />
                        </div>
                    ) : (
                        <span>
                            {signedDate
                                ? formatDate(
                                      appendMidnightTimeToDate(signedDate),
                                      true
                                  )
                                : "Not signed"}
                        </span>
                    );
                },
            },
            {
                accessorKey: "consent_status",
                header: "Consent Status",
                cell: ({ row }) => {
                    const currentStatus = row.original.status as ConsentStatus;
                    return (
                        <Dropdown>
                            <DropdownTrigger asChild>
                                <FilterButton
                                    className={cn(
                                        `capitalize !min-h-7 ![border:none] px-2 ${getConsentStatusTag(
                                            currentStatus
                                        )}`
                                    )}
                                    onClick={(e) => e.stopPropagation()}
                                >
                                    {getConsentStatusDisplay(currentStatus)}
                                </FilterButton>
                            </DropdownTrigger>
                            <DropdownContent width="auto" align="end">
                                {Object.values(ConsentStatus)
                                    .filter(
                                        (status) => status !== currentStatus
                                    )
                                    .map((status) => (
                                        <DropdownItem
                                            key={status}
                                            onClick={(e) => {
                                                handleChangeConsentStatus(
                                                    row.original,
                                                    status
                                                );
                                                e.stopPropagation();
                                            }}
                                        >
                                            {getConsentStatusDisplay(status)}
                                        </DropdownItem>
                                    ))}
                            </DropdownContent>
                        </Dropdown>
                    );
                },
            },
            {
                accessorKey: "expiry_date",
                header: "Expiry Date",
                cell: ({ row }) => {
                    return (
                        <span>
                            {formatDate(row.original.expiry_date, true)}
                        </span>
                    );
                },
            },
            {
                accessorKey: "status",
                header: "Status",
                cell: ({ row }) => {
                    return (
                        <span>
                            {formatExpiryStatus(row.original.days_until_expiry)}
                        </span>
                    );
                },
            },
            {
                accessorKey: "consent_documents",
                header: "Documents Count",
                cell: ({ row }) => {
                    const docs = row.original.consent_documents;
                    return docs.length > 0 ? (
                        <Button
                            variant="normal"
                            size="auto"
                            className="p-0 underline font-normal text-textLink flex items-center gap-2"
                            onClick={(e) => {
                                e.stopPropagation();
                                handleViewDocuments(row.original);
                            }}
                        >
                            <span>
                                {docs.length} document
                                {docs.length !== 1 ? "s" : ""}
                            </span>
                            <span className="text-green-600">📄</span>
                        </Button>
                    ) : (
                        <span className="text-gray-500">No documents</span>
                    );
                },
            },
            {
                accessorKey: "updated_at",
                header: "Updated Date",
                cell: ({ row }) => {
                    return (
                        <span>{formatDate(row.original.updated_at, true)}</span>
                    );
                },
            },
            {
                accessorKey: "actions",
                header: "Actions",
                cell: ({ row }) => {
                    const consent = row.original;
                    const isEditing = editingConsentId === consent.consent_id;

                    return (
                        <div className="flex gap-2 items-center">
                            {isEditing ? (
                                <>
                                    <ActionButton
                                        size="auto"
                                        variant="normal"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            handleCancelEdit();
                                        }}
                                        disabled={
                                            updateConsentMutation.isLoading
                                        }
                                        actionTitle="Cancel"
                                    >
                                        <TimesIcon className="stroke-danger size-3.5" />
                                    </ActionButton>
                                    <ActionButton
                                        size="auto"
                                        variant="normal"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            handleSaveEdit(consent);
                                        }}
                                        disabled={
                                            updateConsentMutation.isLoading
                                        }
                                        actionTitle="Save"
                                    >
                                        <CheckIcon className="stroke-primary size-3.5" />
                                    </ActionButton>
                                </>
                            ) : (
                                <>
                                    <ActionButton
                                        size="auto"
                                        variant="normal"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            handleEditConsent(consent);
                                        }}
                                        actionTitle="Edit dates"
                                    >
                                        <EditIcon className="stroke-text" />
                                    </ActionButton>
                                    <ActionButton
                                        size="auto"
                                        variant="normal"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            handleViewActivityLog(consent);
                                        }}
                                        actionTitle="View activities"
                                    >
                                        <EyeIcon className="stroke-text" />
                                    </ActionButton>
                                    <ActionButton
                                        size="auto"
                                        variant="normal"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            handleOpenUploadModal(
                                                consent.consent_id
                                            );
                                        }}
                                        actionTitle="Upload documents"
                                    >
                                        <UploadIcon className="stroke-text" />
                                    </ActionButton>
                                </>
                            )}
                        </div>
                    );
                },
            },
        ],
        [
            editingConsentId,
            editedSignedDate,
            updateConsentMutation.isLoading,
            handleChangeConsentStatus,
            handleSaveEdit,
            handleEditConsent,
        ]
    );

    const table = useReactTable({
        columns,
        data: data?.data || [],
        state: {
            columnVisibility,
        },
        getCoreRowModel: getCoreRowModel(),
        onColumnVisibilityChange: handleColumnVisibilityChange,
    });

    const selectedColumnsForExport = table
        .getAllLeafColumns()
        .filter((column) => column.getIsVisible())
        .filter(
            (column) =>
                column.id !== "actions" &&
                column?.columnDef?.header?.toString()?.toLowerCase() !==
                    "actions"
        )
        .map(
            (column) =>
                column?.columnDef?.header?.toString()?.toUpperCase() ||
                column.id?.replaceAll("_", " ")?.toUpperCase()
        );

    const { refetch: exportData } = useExportExpiringConsents(
        {
            search_string: search,
            status: statusFilter as ConsentStatus,
            start_date_from: startDateFromFilter || undefined,
            start_date_to: startDateToFilter || undefined,
            selected_columns:
                selectedColumnsForExport && selectedColumnsForExport.length > 0
                    ? selectedColumnsForExport.filter(
                          (col) => col !== "ACTIONS"
                      )
                    : [],
        },
        false
    );

    const handleExport = async () => {
        setIsExporting(true);
        try {
            await exportData();
        } finally {
            setIsExporting(false);
        }
    };

    return (
        <>
            <Card classNames="flex flex-col gap-[--spacing-items] mb-[--spacing-header]">
                <div className="flex justify-between items-center">
                    <SearchInput
                        placeholder="Search by client name, email, phone"
                        containerClass="w-[400px]"
                        onChange={handleSetSearchString}
                        defaultValue={searchString}
                    />
                    <div className="flex gap-2">
                        <Button
                            variant="secondary"
                            onClick={handleExport}
                            disabled={isExporting || data?.data.length === 0}
                        >
                            {isExporting ? "Exporting..." : "Export"}
                        </Button>
                    </div>
                </div>

                <div className="flex items-center gap-[--spacing-items] flex-wrap">
                    <Dropdown>
                        <DropdownTrigger asChild>
                            <FilterButton>
                                {statusFilter
                                    ? getConsentStatusDisplay(
                                          statusFilter as ConsentStatus
                                      )
                                    : "Status"}
                            </FilterButton>
                        </DropdownTrigger>
                        <DropdownContent width={150}>
                            <DropdownItem
                                onClick={() => handleSetStatusFilter("")}
                                isSelected={!statusFilter}
                                closeMenuOnSelect
                            >
                                All Statuses
                            </DropdownItem>
                            {Object.values(ConsentStatus).map((status) => (
                                <DropdownItem
                                    key={status}
                                    onClick={() =>
                                        handleSetStatusFilter(status)
                                    }
                                    isSelected={statusFilter === status}
                                    closeMenuOnSelect
                                >
                                    {getConsentStatusDisplay(status)}
                                </DropdownItem>
                            ))}
                        </DropdownContent>
                    </Dropdown>

                    <Popover>
                        <PopoverTrigger asChild>
                            <FilterButton>Start Date Range</FilterButton>
                        </PopoverTrigger>
                        <PopoverContent align="end">
                            <div className="mb-1">
                                <DatePicker
                                    label="From"
                                    placeholderText="MM/DD/YYYY"
                                    selected={startDateFromFilterToDate}
                                    onChange={handleSetStartDateFromFilter}
                                    maxDate={
                                        startDateToFilterToDate ||
                                        new Date(Date.now())
                                    }
                                    isClearable
                                />
                            </div>
                            <DatePicker
                                label="To"
                                placeholderText="MM/DD/YYYY"
                                selected={startDateToFilterToDate}
                                onChange={handleSetStartDateToFilter}
                                className="hover:border-primary"
                                minDate={startDateFromFilterToDate}
                                isClearable
                            />
                        </PopoverContent>
                    </Popover>
                </div>
                <TableFiltersDisplay
                    appliedFilters={[
                        {
                            key: "status",
                            values: statusFilter
                                ? [
                                      getConsentStatusDisplay(
                                          statusFilter as ConsentStatus
                                      ),
                                  ]
                                : [],
                        },
                    ]}
                    wrapperClass="mt-[--spacing-items]"
                    localStorageKey="expiringconsents_filters"
                />
            </Card>

            {isLoading && <Loader />}

            {error && (
                <ListState
                    isError
                    stateHelperText="Try reloading this page to solve this issue"
                    errorMsg={
                        error?.response?.data.message ||
                        "Cannot display expiring consents at this time please try again later"
                    }
                />
            )}

            {isSuccess && data.data.length === 0 && (
                <ListState
                    stateHelperText="No expiring consents found"
                    isError={false}
                />
            )}

            {isSuccess && data.data.length > 0 && (
                <Table
                    data={data?.data}
                    columns={columns}
                    showSizeFilter
                    isColumnVisible={columnVisibility}
                    showColumnVisibilityFilter
                    columnVisibilityDropdownItems={
                        <>
                            {table.getAllLeafColumns().map((column) => {
                                if (column.id === "actions") return null;
                                return (
                                    <DropdownItem
                                        key={column.id}
                                        onClick={() =>
                                            column.toggleVisibility()
                                        }
                                        className="*:!uppercase"
                                        isMulti
                                        itemValue={column.id}
                                        values={table
                                            .getVisibleLeafColumns()
                                            .map((col) => col.id)}
                                    >
                                        {column?.columnDef?.header?.toString() ||
                                            column.id?.replaceAll("_", " ")}
                                    </DropdownItem>
                                );
                            })}
                        </>
                    }
                />
            )}

            {showDocumentViewer && (
                <Dialog
                    open={showDocumentViewer}
                    onOpenChange={handleCloseDocumentViewer}
                >
                    <DialogContent
                        variant="drawer"
                        title="Consent document"
                        handleCloseDialog={handleCloseDocumentViewer}
                        showFooter={false}
                        wrapperClass="w-[1180px] max-w-[calc(100vw-24px)]"
                        classNames="!overflow-hidden !p-0"
                    >
                        {selectedDocuments.length > 0 &&
                        selectedConsentForViewer ? (
                            <ConsentDocumentViewer
                                consent={selectedConsentForViewer}
                                document={
                                    selectedDocuments[currentDocumentIndex]
                                }
                                documentIndex={currentDocumentIndex}
                                documentCount={selectedDocuments.length}
                                onPreviousDocument={() =>
                                    setCurrentDocumentIndex((previous) =>
                                        previous > 0
                                            ? previous - 1
                                            : selectedDocuments.length - 1
                                    )
                                }
                                onNextDocument={() =>
                                    setCurrentDocumentIndex((previous) =>
                                        previous < selectedDocuments.length - 1
                                            ? previous + 1
                                            : 0
                                    )
                                }
                            />
                        ) : (
                            <div className="flex items-center justify-center h-64 text-gray-500">
                                No documents available
                            </div>
                        )}
                    </DialogContent>
                </Dialog>
            )}

            {selectedConsentForUpload ? (
                <Dialog open={dialogInView === "upload"}>
                    <DialogContent
                        variant="center"
                        title="Upload Consent Documents"
                        handleCloseDialog={handleCloseUploadModal}
                        saveText="Upload"
                        cancelText="Cancel"
                        isSubmitBtnDisabled={isUploadDocumentMutating > 0}
                        isCancelBtnDisabled={isUploadDocumentMutating > 0}
                        submitBtnFormValue="upload-consent-documents"
                    >
                        <UploadDocumentForm
                            consentId={selectedConsentForUpload}
                            onFormSubmit={handleCloseUploadModal}
                        />
                    </DialogContent>
                </Dialog>
            ) : null}

            {showActivityLogModal && (
                <ActivityLogModal
                    isOpen={showActivityLogModal}
                    consent={
                        selectedConsentForActivity as unknown as IConsentReport
                    }
                    onClose={() => {
                        setShowActivityLogModal(false);
                        setSelectedConsentForActivity(null);
                    }}
                />
            )}

            {selectedConsentForCorrection ? (
                <Dialog open>
                    <DialogContent
                        variant="center"
                        title="Request Consent Correction"
                        handleCloseDialog={() =>
                            setSelectedConsentForCorrection(null)
                        }
                        saveText="Send Correction Request"
                        cancelText="Cancel"
                        submitBtnFormValue="request-consent-correction"
                        isCancelBtnDisabled={isUpdateConsentMutating > 0}
                        isSubmitBtnDisabled={isUpdateConsentMutating > 0}
                    >
                        <RequestConsentCorrectionForm
                            consentId={selectedConsentForCorrection.consent_id}
                            onFormSubmit={() =>
                                setSelectedConsentForCorrection(null)
                            }
                            queryKey="expiring-consents"
                        />
                    </DialogContent>
                </Dialog>
            ) : null}
        </>
    );
}
